import React from 'react';
import { motion } from 'framer-motion';
import {
  Mic, MicOff, Sparkles, Loader, Save, X,
} from 'lucide-react';

// The big Add / Edit Artwork form, lifted out of ArtworkManager.jsx so the
// parent stays focused on data orchestration. Fully controlled — owns no
// state of its own; `formData` + `setFormData` are passed down.
//
// Props:
//   editingArtwork   — null when adding, the row being edited otherwise
//   formData         — controlled form values (see artworkFormData.js)
//   setFormData      — setter from useState in the parent
//   artists          — list of artists for the Artist dropdown
//   onSubmit         — form submit handler (calls API + refresh + reset)
//   onCancel         — reset + close
//   isListening      — mic state from useSpeechToText
//   onToggleVoice    — toggle handler from useSpeechToText
//   isGenerating     — true while AI description is loading
//   onGenerateAI     — call the /api/ai/artwork-description endpoint
const ArtworkForm = ({
  editingArtwork,
  formData,
  setFormData,
  artists,
  onSubmit,
  onCancel,
  isListening,
  onToggleVoice,
  isGenerating,
  onGenerateAI,
}) => {
  // Functional update — several handlers (e.g. the Status select) call
  // setField twice in a row; spreading the stale `formData` dropped the
  // first update (status) and only kept the last (available).
  const setField = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 mb-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-3xl font-light">
          {editingArtwork ? 'Edit Artwork' : 'Add New Artwork'}
        </h2>
        <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg">
          <X className="w-5 h-5" />
        </button>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium mb-4">Basic Information</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Artist *</label>
              <select
                value={formData.artistId}
                onChange={(e) => {
                  const artist = artists.find((a) => a.id === e.target.value);
                  setFormData({
                    ...formData,
                    artistId: e.target.value,
                    artistName: artist ? artist.name : '',
                  });
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              >
                <option value="">Select Artist</option>
                {artists.map((artist) => (
                  <option key={artist.id} value={artist.id}>{artist.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Or Enter Artist Name</label>
              <input
                type="text"
                value={formData.artistName}
                onChange={(e) => setField('artistName', e.target.value)}
                placeholder="Artist name if not in list"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setField('title', e.target.value)}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Year</label>
              <input
                type="text"
                value={formData.year}
                onChange={(e) => setField('year', e.target.value)}
                placeholder="e.g., 2024"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Medium</label>
              <input
                type="text"
                value={formData.medium}
                onChange={(e) => setField('medium', e.target.value)}
                placeholder="e.g., Oil on canvas"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Dimensions</label>
              <input
                type="text"
                value={formData.dimensions}
                onChange={(e) => setField('dimensions', e.target.value)}
                placeholder="e.g., 100 x 80 cm"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setField('category', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              >
                <option value="">Select Category</option>
                <option value="painting">Painting</option>
                <option value="photography">Photography</option>
                <option value="sculpture">Sculpture</option>
                <option value="print">Print</option>
                <option value="drawing">Drawing</option>
                <option value="mixed-media">Mixed Media</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Edition</label>
              <input
                type="text"
                value={formData.edition}
                onChange={(e) => setField('edition', e.target.value)}
                placeholder="e.g., 1/10, Unique"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Description with AI */}
        <div className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium">Description & Alt Text</h3>
            <div className="flex gap-2">
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onToggleVoice}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                  isListening
                    ? 'bg-red-500 text-white'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                {isListening ? 'Stop' : 'Voice'}
              </motion.button>
              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onGenerateAI}
                disabled={isGenerating}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg flex items-center gap-2 disabled:opacity-50"
              >
                {isGenerating ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                AI Generate
              </motion.button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setField('description', e.target.value)}
                rows={6}
                placeholder="Describe the artwork, its inspiration, techniques used, etc."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
              {isListening && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-2">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  Recording...
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Alt Text (for accessibility)</label>
              <input
                type="text"
                value={formData.altText}
                onChange={(e) => setField('altText', e.target.value)}
                placeholder="Brief description of the image for screen readers"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Pricing */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium mb-4">Pricing</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Price (£)</label>
              <input
                type="number"
                value={formData.price}
                onChange={(e) => setField('price', e.target.value)}
                placeholder="5000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Price Display</label>
              <input
                type="text"
                value={formData.priceDisplay}
                onChange={(e) => setField('priceDisplay', e.target.value)}
                placeholder="£5,000 or Price on request"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Image */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium mb-4">Image</h3>
          <div>
            <label className="block text-sm font-medium mb-2">Image URL</label>
            <input
              type="text"
              value={formData.imageUrl}
              onChange={(e) => setField('imageUrl', e.target.value)}
              placeholder="https://... or select from Media Library"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
            <p className="text-xs text-gray-500 mt-1">Or go to Media Library to upload</p>
          </div>
        </div>

        {/* Additional Details */}
        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-lg font-medium mb-4">Additional Details</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Provenance</label>
              <textarea
                value={formData.provenance}
                onChange={(e) => setField('provenance', e.target.value)}
                rows={2}
                placeholder="Ownership history"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Exhibition History</label>
              <textarea
                value={formData.exhibition_history}
                onChange={(e) => setField('exhibition_history', e.target.value)}
                rows={2}
                placeholder="Where this work has been exhibited"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Literature</label>
              <textarea
                value={formData.literature}
                onChange={(e) => setField('literature', e.target.value)}
                rows={2}
                placeholder="Publications featuring this work"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              />
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-gray-500">Status</span>
            <select
              value={
                (!formData.price && (formData.status === 'available' || !formData.status))
                  ? 'enquire'
                  : (formData.status || (formData.available === false ? 'sold' : 'available'))
              }
              onChange={(e) => {
                const next = e.target.value;
                if (next === 'enquire') {
                  setField('price', '');
                  setField('priceDisplay', '');
                  setField('status', 'available');
                  setField('available', true);
                  setField('on_hold_reason', '');
                } else {
                  setField('status', next);
                  setField('available', next === 'available');
                  // Clear the memo when leaving on_hold so it doesn't
                  // linger in the DB (and re-appear if the piece goes
                  // on_hold again for a different collector).
                  if (next !== 'on_hold') setField('on_hold_reason', '');
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
              data-testid="artwork-status-select"
            >
              <option value="available">Available — show price</option>
              <option value="on_hold">On Hold — hidden from buy</option>
              <option value="sold">Sold — SOLD badge</option>
              <option value="enquire">Enquire (no price) — clear price to switch</option>
            </select>
            <span className="text-[11px] text-gray-500">
              Cards with no price automatically show "Enquire". Set price to £0 or blank to switch.
            </span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-widest text-gray-500">VAT</span>
            <select
              value={formData.vatScheme || 'standard'}
              onChange={(e) => setField('vatScheme', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black text-sm"
              data-testid="artwork-vat-scheme-select"
            >
              <option value="standard">Standard rate — UK buyers pay +20% VAT at checkout</option>
              <option value="margin">Margin Scheme (resale) — price is all-in, no VAT added</option>
            </select>
            <span className="text-[11px] text-gray-500">
              Use Margin Scheme for secondary-market works you bought in to resell.
            </span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.featured}
              onChange={(e) => setField('featured', e.target.checked)}
              className="w-4 h-4"
              data-testid="artwork-featured-checkbox"
            />
            <span className="text-sm font-medium">Featured — pin to Homepage "Latest"</span>
          </label>
        </div>

        {/* On Hold reason — only when status is on_hold. Free-form note so
            the admin remembers who a piece is reserved for. Never shown on
            the public site. */}
        {formData.status === 'on_hold' && (
          <div>
            <label className="block text-sm font-medium mb-2">
              On Hold Reason <span className="text-gray-400 font-normal">(admin only)</span>
            </label>
            <input
              type="text"
              value={formData.on_hold_reason}
              onChange={(e) => setField('on_hold_reason', e.target.value)}
              placeholder='e.g. "Reserved for John Smith until 15 March"'
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="artwork-on-hold-reason"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Visible only in the admin dashboard so you remember who to chase.
            </p>
          </div>
        )}

        {/* Legacy field wrapper kept below so unrelated JSX still closes cleanly */}
        <div className="hidden">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.featured}
              onChange={(e) => setField('featured', e.target.checked)}
              className="w-4 h-4"
            />
            <span className="text-sm font-medium">Featured Artwork</span>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            className="px-8 py-3 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center gap-2"
            data-testid="artwork-form-submit-btn"
          >
            <Save className="w-5 h-5" />
            {editingArtwork ? 'Update Artwork' : 'Create Artwork'}
          </motion.button>
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default ArtworkForm;
